import { createClient } from "@/utils/supabase/client";
import { toast } from "@/hooks/use-toast";

const supabase = createClient();

// Helper function to clear all auth-related cookies to prevent login loops
const clearAuthCookies = () => {
  if (typeof document !== "undefined") {
    // Clear all known auth cookies with different paths and domains
    const cookiesToClear = [
      "sb-access-token",
      "sb-refresh-token",
      "supabase-auth-token",
      "__supabase_session",
    ];

    const paths = ["/", ""];
    const domain = window.location.hostname;
    const domains = [domain, `.${domain}`, ""];

    cookiesToClear.forEach((name) => {
      paths.forEach((path) => {
        domains.forEach((d) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${
            d ? `; domain=${d}` : ""
          }`;
        });
      });
    });
  }
};

const useAuthStore = (set, get) => {
  return {
    session: null,
    loading: false,
    error: null,

    // Basic loading reset function for compatibility
    forceLoadingReset: () => {
      set({ loading: false });
    },

    // Clear auth session and cookies
    clearAuth: async () => {
      try {
        // Sign out using Supabase
        await supabase.auth.signOut();

        // Clear cookies manually as well to ensure they're gone
        clearAuthCookies();

        // Update store state
        set({ session: null, loading: false });
      } catch (error) {
        console.error("Error clearing auth:", error);
        // Still clear cookies and state even if Supabase error
        clearAuthCookies();
        set({ session: null, loading: false });
      }
    },

    // Check if user exists in the database - only used at initial login
    checkUserExists: async (userId) => {
      try {
        if (!userId) return { exists: false };

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/check-user-exists`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
              "Content-Type": "application/json",
              "x-client-info": "@supabase/auth-helpers-nextjs",
            },
            body: JSON.stringify({ user_id: userId }),
          }
        );

        // If server error (like 500), the user likely doesn't exist
        if (!response.ok) {
          console.error(
            "User existence check failed with status:",
            response.status
          );
          // Clear auth session completely on server error
          await get().clearAuth();
          return { exists: false, error: `Server error: ${response.status}` };
        }

        const data = await response.json();

        if (!data.exists || data.error) {
          console.error("User not found or error:", data.error);
          // Clear auth session if user doesn't exist
          await get().clearAuth();
          return { exists: false, error: data.error };
        }

        return { exists: true };
      } catch (error) {
        console.error("Error checking user existence:", error);
        return { exists: false, error: error.message };
      }
    },

    // Simple initialization - just get the session once
    initialize: async () => {
      try {
        set({ loading: true });

        // Get the session
        const { data, error } = await supabase.auth.getSession();

        if (error) throw error;

        // Set session state
        set({
          session: data.session,
          loading: false,
        });

        // Only verify user exists at initial login
        if (data.session?.user?.id) {
          const { exists, error: existsError } = await get().checkUserExists(
            data.session.user.id
          );

          if (!exists) {
            console.error("User does not exist in database:", existsError);
            set({ loading: false }); // Ensure loading is reset
            return; // CheckUserExists already clears auth
          }

          // Fetch user settings if session exists and user exists
          try {
            await get().fetchUserSettings();
          } catch (e) {
            console.error("Error fetching initial user settings:", e);
            // Continue even if settings fetch fails
          }
        }

        // Set up auth state listener for session changes
        const { data: authListener } = supabase.auth.onAuthStateChange(
          (event, session) => {
            console.log("Auth state change event:", event);

            // Handle different auth events appropriately
            switch (event) {
              case "SIGNED_IN":
                // New sign in - always fetch fresh user settings
                set({ session });
                if (session?.user?.id) {
                  console.log(
                    "New sign in detected, fetching fresh user settings"
                  );
                  setTimeout(() => {
                    get()
                      .fetchUserSettings()
                      .catch((err) => {
                        console.error(
                          "Error fetching user settings after sign in:",
                          err
                        );
                      });
                  }, 0);
                }
                break;

              case "SIGNED_OUT":
                // Clear everything on sign out
                console.log("User signed out, clearing session and settings");
                set({ session: null });
                break;

              case "TOKEN_REFRESHED":
              case "USER_UPDATED":
                // For token refresh or user update, preserve user settings
                const currentSession = get().session;
                const userSettings = currentSession?.user?.user_settings;

                // Only preserve settings if it's the same user
                if (
                  userSettings &&
                  session?.user?.id &&
                  currentSession?.user?.id === session.user.id
                ) {
                  console.log(
                    "Preserving user settings during token refresh/update"
                  );
                  const updatedSession = {
                    ...session,
                    user: {
                      ...session.user,
                      user_settings: userSettings,
                    },
                  };
                  set({ session: updatedSession });
                } else {
                  // Different user or no settings, just update session
                  set({ session });
                }
                break;

              default:
                // For any other events, just update the session
                set({ session });
                break;
            }
          }
        );

        // Return cleanup function
        return () => {
          if (authListener?.subscription?.unsubscribe) {
            authListener.subscription.unsubscribe();
          }
        };
      } catch (error) {
        console.error("Error getting session:", error);
        await get().clearAuth();
      } finally {
        // Make absolutely sure loading state is reset
        set({ loading: false });
      }
    },

    // Basic session refresh - only updates the session without checking existence
    refreshSession: async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) throw error;

        // Just update the session without extra checks
        set({ session: data.session });

        // If we have a session, fetch user settings
        if (data.session?.user?.id) {
          await get().fetchUserSettings();
        }
      } catch (error) {
        console.error("Error refreshing session:", error);
        await get().clearAuth();
      }
    },

    // User settings
    fetchUserSettings: async () => {
      try {
        const session = get().session;
        if (!session?.user?.id) {
          console.log("No session or user ID available for fetching settings");
          return null;
        }

        console.log("Fetching user settings for user ID:", session.user.id);

        const { data, error } = await supabase
          .from("user_settings")
          .select("*")
          .eq("id", session.user.id) // Changed back to "id" as confirmed by user
          .single();

        if (error) {
          console.error("Supabase error fetching user settings:", error);
          throw error;
        }

        if (!data) {
          console.log("No user settings found for user:", session.user.id);
          return null;
        }

        // Update session with user settings
        const updatedSession = {
          ...session,
          user: {
            ...session.user,
            user_settings: data,
          },
        };

        set({ session: updatedSession });
        return data;
      } catch (error) {
        console.error("Error fetching user settings:", error);
        // Don't silently fail - log detailed error
        toast({
          title: "Settings Error",
          description:
            "Could not load your user settings. Some features may be limited.",
          variant: "destructive",
        });
        return null;
      }
    },

    // Sign in
    signIn: async (credentials) => {
      try {
        set({ loading: true, error: null });

        const { data, error } = await supabase.auth.signInWithPassword(
          credentials
        );

        if (error) throw error;

        // Verify user exists in database
        const { exists, error: existsError } = await get().checkUserExists(
          data.session?.user?.id
        );

        if (!exists) {
          console.error("User not found in database:", existsError);
          // Clearauth already done in checkUserExists
          return {
            success: false,
            error: "User account not found. Please contact support.",
          };
        }

        set({ session: data.session, loading: false });

        return { success: true };
      } catch (error) {
        console.error("Sign in error:", error);
        set({ error: error.message, loading: false });

        return {
          success: false,
          error: error.message,
        };
      }
    },

    // Sign out
    signOut: async () => {
      try {
        set({ loading: true });

        // Use our more comprehensive clearAuth function
        await get().clearAuth();

        return { success: true };
      } catch (error) {
        console.error("Sign out error:", error);
        set({ error: error.message, loading: false });

        return { success: false, error: error.message };
      }
    },

    // Sign up
    signUp: async ({ email, password }) => {
      try {
        set({ loading: true });

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        set({ loading: false });

        // Show toast message
        if (data?.session) {
          set({ session: data.session });
          toast({
            title: "Success",
            description: "Account created successfully!",
            variant: "success",
          });
        } else {
          toast({
            title: "Success",
            description: "Please check your email to verify your account",
            variant: "success",
          });
        }

        return { success: true, session: data.session };
      } catch (error) {
        console.error("Sign up error:", error);
        set({ loading: false });

        toast({
          title: "Error",
          description: error.message || "Failed to sign up",
          variant: "destructive",
        });

        return { success: false, error: error.message };
      }
    },

    // Sign in with provider
    signInWithProvider: async (provider, options = {}) => {
      try {
        const { redirectTo, ...otherOptions } = options;

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo:
              redirectTo || `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
            queryParams: {
              access_type: "offline",
              prompt: "select_account",
            },
            scopes: "email profile",
            skipBrowserRedirect: false,
          },
        });

        if (error) throw error;

        return { success: true, data };
      } catch (error) {
        console.error("Provider sign in error:", error);
        return {
          success: false,
          error: error.message || `Failed to sign in with ${provider}`,
        };
      }
    },

    // Sign in with ID token (for Google One Tap)
    signInWithIdToken: async (provider, token, nonce) => {
      try {
        set({ loading: true });

        const { data, error } = await supabase.auth.signInWithIdToken({
          provider,
          token,
          nonce,
        });

        if (error) throw error;

        set({ session: data.session, loading: false });

        return { success: true, data };
      } catch (error) {
        console.error("ID token sign in error:", error);
        set({ error: error.message, loading: false });

        return {
          success: false,
          error: error.message || `Failed to sign in with ${provider} ID token`,
        };
      }
    },

    // Check if email exists
    checkEmailExists: async (email) => {
      try {
        const { data, error } = await supabase.rpc("check_if_email_exists", {
          email,
        });

        if (error) throw error;

        return {
          exists: data,
          email: data ? email : null,
          error: null,
        };
      } catch (error) {
        console.error("Error checking email:", error);
        return {
          error: "Failed to check email",
          exists: false,
        };
      }
    },

    // Check authentication method for a user
    checkAuthMethod: async (email) => {
      try {
        const { data, error } = await supabase.rpc("check_auth_method", {
          p_email: email,
        });

        if (error) throw error;

        return {
          data,
          error: null,
        };
      } catch (error) {
        console.error("Error checking auth method:", error);
        return {
          error: error.message || "Failed to check authentication method",
          data: null,
        };
      }
    },

    // Check if email is confirmed
    checkEmailConfirmed: async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) throw error;

        const confirmed = !!data.session?.user?.email_confirmed_at;

        if (confirmed) {
          toast({
            title: "Success",
            description: "Email confirmed successfully",
            variant: "success",
          });
        }

        return { confirmed };
      } catch (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to check email confirmation",
          variant: "destructive",
        });

        return { confirmed: false, error: error.message };
      }
    },
  };
};

export default useAuthStore;
